import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation } from 'react-query';
import { useDebounce } from '@/lib/useDebounce';
import Modal from '@/components/layout/Modal';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Badge from '@/components/layout/Badge';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Icon from '@mdi/react';
import { mdiAlert, mdiCheck, mdiClose, mdiLoading, mdiPencil, mdiPlus, mdiToggleSwitch, mdiToggleSwitchOffOutline, mdiTrashCan } from '@mdi/js';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { queryClient } from '@/queryClient';
import { AsnDTO, CategoryDTO, FeedDTO, PolicyDTO, RuleDTO, RuleAction, RuleType, PolicyScope, PolicyTipo } from '@webip/shared/src/dto/firewall';
import { ApplianceDTO, DeviceGroupRefDTO, NetworkLanDTO } from '@webip/shared/src/dto/appliance';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import CampoSeletorAsync from '@/components/Formulario/CampoSeletorAsync';

type Props = {
    policy: PolicyDTO;
    onClose: () => void;
};

type ResponseFeeds = {
    ok?: boolean;
    result: FeedDTO[];
};

type ResponseCategories = {
    ok?: boolean;
    result: CategoryDTO[];
};

type ResponseAsns = {
    ok?: boolean;
    result: AsnDTO[];
};

type AsnCatalogEntry = { asn: number; nome: string; pais: string | null };
type ResponseAsnCatalog = { ok?: boolean; result: AsnCatalogEntry[] };

const ACTION_VARIANTS: Record<RuleAction, 'success' | 'danger'> = {
    allow: 'success',
    block: 'danger',
};

const RULE_TYPE_OPTIONS = [
    { id: 'domain', label: 'Domínio' },
    { id: 'https', label: 'Navegação (HTTPS)' },
    { id: 'cidr', label: 'IP / CIDR' },
    { id: 'port', label: 'Porta' },
    { id: 'category', label: 'Categoria' },
    { id: 'feed', label: 'Feed de IPs' },
    { id: 'asn', label: 'ASN' },
];

// Tipos cujo campo "Alvo" é o mesmo texto livre (domínio/IP/porta) — trocar
// entre eles não deve limpar o que o usuário já digitou.
const FREE_TEXT_RULE_TYPES: RuleType[] = ['domain', 'https', 'cidr', 'port'];

const BULK_MODE_OPTIONS = [
    { id: 'match', label: 'Alvo (domínio/IP/porta)' },
    { id: 'category', label: 'Categorias' },
    { id: 'feed', label: 'Feed de IPs' },
];

function CategoryPerformanceWarning({ message }: { message: string | null }) {
    if (!message) return null;
    return (
        <HBox style={{ gap: '8px', padding: '8px 10px', background: 'var(--tc-background-color-warning-subtle)', border: '1px solid var(--tc-border-color-warning-subtle)', borderRadius: '6px', alignItems: 'flex-start' }}>
            <Icon path={mdiAlert} size={0.75} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--tc-color-warning-600)' }} />
            <span style={{ fontSize: '0.82em', color: 'var(--tc-text-color-warning-emphasis)', lineHeight: '1.4' }}>
                <strong>Atenção:</strong> {message}
            </span>
        </HBox>
    );
}

const RULE_ACTION_OPTIONS = [
    { id: 'block', label: 'Bloquear' },
    { id: 'allow', label: 'Permitir' },
];

const DEFAULT_ACTION_OPTIONS = [
    { id: 'allow', label: 'Permitir' },
    { id: 'deny', label: 'Negar' },
];

const BLANK_RULE = {
    type: 'domain' as RuleType,
    target: '',
    action: 'block' as RuleAction,
    schedule: '',
    source: 'manual' as 'feed' | 'manual',
    feedId: null as number | null,
    categoryId: null as number | null,
};

type RulePayloadType = RuleType | 'match';
type RulePayload = {
    type: RulePayloadType;
    target: string;
    action: RuleAction;
    schedule: string;
    source?: 'feed' | 'manual';
    feedId?: number | null;
    categoryId?: number | null;
    asnId?: number | null;
};

type BulkLineStatus = 'ok' | 'invalid' | 'duplicate' | 'redundant';
type BulkLineAnalysis = {
    lineNumber: number;
    target: string;
    status: BulkLineStatus;
    message: string;
};

function inferRuleTypeFromTarget(rawTarget: string): RuleType {
    const target = rawTarget.trim();
    if (target.startsWith(':')) return 'port';

    const beforePort = target.includes(':') ? target.split(':')[0] : target;
    if (/^\d+$/.test(beforePort)) return 'port';
    if (/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(beforePort)) return 'cidr';
    return 'domain';
}

type ParsedMatchTarget = {
    kind: 'domain' | 'ip' | 'port-only';
    base?: string;
    port?: number;
    // Só em alvo com host ("ip:porta/udp"); sem sufixo o adapter assume tcp.
    // port-only não tem protocolo (casa TCP e UDP).
    proto?: string;
};

function parseMatchTarget(raw: string): ParsedMatchTarget | null {
    const value = raw.trim().toLowerCase();
    if (!value) return null;

    if (value.startsWith(':')) {
        const port = Number(value.slice(1));
        return Number.isInteger(port) ? { kind: 'port-only', port } : null;
    }

    const [left, right] = value.split(':');
    const [portStr, protoStr] = right ? right.split('/') : [];
    const parsedPort = portStr ? Number(portStr) : undefined;

    if (/^\d+$/.test(left) && !right) {
        return { kind: 'port-only', port: Number(left) };
    }

    const isIp = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(left);
    return {
        kind: isIp ? 'ip' : 'domain',
        base: left,
        port: parsedPort,
        proto: parsedPort !== undefined ? (protoStr || 'tcp') : undefined,
    };
}

function ipToInt(ip: string): number {
    return ip.split('.').reduce((acc, octet) => (acc << 8) + Number(octet), 0) >>> 0;
}

function getIPRange(cidrOrIp: string): { start: number; end: number } {
    const [ip, cidrStr] = cidrOrIp.split('/');
    const cidr = cidrStr ? Number(cidrStr) : 32;
    const ipInt = ipToInt(ip);
    const mask = cidr === 0 ? 0 : (0xffffffff << (32 - cidr)) >>> 0;
    const start = ipInt & mask;
    const end = start | (~mask >>> 0);
    return { start, end };
}

function isDomainContainedIn(targetDomain: string, containerDomain: string): boolean {
    if (targetDomain === containerDomain) return true;
    if (targetDomain.endsWith(`.${containerDomain}`)) return true;
    if (targetDomain.startsWith(`${containerDomain}.`)) return true;
    return false;
}

function doesPortContain(containerPort?: number, targetPort?: number, containerProto?: string, targetProto?: string): boolean {
    // Sem porta no container significa "todas as portas".
    if (containerPort === undefined) return true;
    if (targetPort === undefined) return false;
    // Sem protocolo no container (port-only) significa TCP e UDP.
    if (containerProto !== undefined && containerProto !== targetProto) return false;
    return containerPort === targetPort;
}

function isTargetContainedIn(targetStr: string, containerStr: string): boolean {
    const target = parseMatchTarget(targetStr);
    const container = parseMatchTarget(containerStr);
    if (!target || !container) return false;

    if (container.kind === 'port-only') {
        return doesPortContain(container.port, target.port);
    }

    if (target.kind === 'port-only') {
        return false;
    }

    if (container.kind !== target.kind) {
        return false;
    }

    const sameBaseKind = container.kind === 'ip'
        ? (() => {
            const targetRange = getIPRange(target.base as string);
            const containerRange = getIPRange(container.base as string);
            return containerRange.start <= targetRange.start && containerRange.end >= targetRange.end;
        })()
        : isDomainContainedIn(target.base as string, container.base as string);

    if (!sameBaseKind) return false;
    return doesPortContain(container.port, target.port, container.proto, target.proto);
}

function validateMatchTarget(rawTarget: string): { ok: true } | { ok: false; message: string } {
    const target = rawTarget.trim();
    if (!target) return { ok: false, message: 'Informe um alvo' };

    if (target.startsWith(':')) {
        const port = target.slice(1);
        const portNum = Number(port);
        if (!/^\d+$/.test(port) || portNum < 1 || portNum > 65535) {
            return { ok: false, message: 'Porta inválida (use 1-65535)' };
        }
        return { ok: true };
    }

    const [left, right, ...rest] = target.split(':');
    if (rest.length > 0) return { ok: false, message: 'Formato inválido. Use domínio:porta, ip/cidr:porta ou :porta' };

    if (right) {
        const [port, proto, ...extra] = right.split('/');
        const portNum = Number(port);
        if (!/^\d+$/.test(port) || portNum < 1 || portNum > 65535 || extra.length > 0) {
            return { ok: false, message: 'Porta inválida (use 1-65535)' };
        }
        if (proto !== undefined && !/^(tcp|udp)$/i.test(proto)) {
            return { ok: false, message: 'Protocolo inválido (use tcp ou udp)' };
        }
    }

    if (/^\d+$/.test(left)) {
        const portNum = Number(left);
        if (portNum < 1 || portNum > 65535) {
            return { ok: false, message: 'Porta inválida (use 1-65535)' };
        }
        return { ok: true };
    }

    if (/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(left)) {
        const [ip, cidr] = left.split('/');
        const octets = ip.split('.').map(Number);
        if (octets.some(o => Number.isNaN(o) || o < 0 || o > 255)) {
            return { ok: false, message: 'IP inválido' };
        }
        if (cidr !== undefined) {
            const cidrNum = Number(cidr);
            if (!/^\d+$/.test(cidr) || cidrNum < 0 || cidrNum > 32) {
                return { ok: false, message: 'CIDR inválido (use 0-32)' };
            }
        }
        return { ok: true };
    }

    if (!/^[a-zA-Z0-9*.-]+$/.test(left)) {
        return { ok: false, message: 'Domínio inválido' };
    }

    return { ok: true };
}

function analyzeBulkTargets(raw: string, existingTargets: string[] = []): BulkLineAnalysis[] {
    const lines = raw.split('\n');
    const analyzed: BulkLineAnalysis[] = [];
    const existingNormalizedSet = new Set(
        existingTargets
            .map(target => target.trim().toLowerCase())
            .filter(Boolean),
    );
    const seenInInput = new Set<string>();
    const existingContainers = existingTargets
        .map(target => target.trim())
        .filter(Boolean);

    for (let index = 0; index < lines.length; index++) {
        const target = lines[index].trim();
        if (!target) continue;

        const validation = validateMatchTarget(target);
        if (!validation.ok) {
            analyzed.push({
                lineNumber: index + 1,
                target,
                status: 'invalid',
                message: validation.message,
            });
            continue;
        }

        const key = target.toLowerCase();
        if (existingNormalizedSet.has(key)) {
            analyzed.push({
                lineNumber: index + 1,
                target,
                status: 'duplicate',
                message: 'Duplicado (já existe na política)',
            });
            continue;
        }

        if (seenInInput.has(key)) {
            analyzed.push({
                lineNumber: index + 1,
                target,
                status: 'duplicate',
                message: 'Duplicado (será ignorado)',
            });
            continue;
        }

        const redundantByExisting = existingContainers.find(container => isTargetContainedIn(target, container));
        if (redundantByExisting) {
            analyzed.push({
                lineNumber: index + 1,
                target,
                status: 'redundant',
                message: `Redundante (coberto por regra existente: ${redundantByExisting})`,
            });
            continue;
        }

        // Verifica se está contido em outro alvo anterior
        let redundantByInput: string | null = null;
        for (const prevAnalysis of analyzed) {
            if (prevAnalysis.status === 'ok' && isTargetContainedIn(target, prevAnalysis.target)) {
                redundantByInput = prevAnalysis.target;
                break;
            }
        }

        if (redundantByInput) {
            analyzed.push({
                lineNumber: index + 1,
                target,
                status: 'redundant',
                message: `Redundante (coberto por ${redundantByInput})`,
            });
            continue;
        }

        seenInInput.add(key);
        analyzed.push({
            lineNumber: index + 1,
            target,
            status: 'ok',
            message: 'Formato válido',
        });
    }

    return analyzed;
}

export function PolicyEditorModal({ policy, onClose }: Props) {
    const [newRule, setNewRule] = useState({ ...BLANK_RULE });
    const [newRuleFeedId, setNewRuleFeedId] = useState<number | null>(null);
    const [newRuleCategoryId, setNewRuleCategoryId] = useState<number | null>(null);
    const [newRuleAsnInput, setNewRuleAsnInput] = useState('');
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<RuleDTO | null>(null);
    const [editRulePayload, setEditRulePayload] = useState({ ...BLANK_RULE });
    const [editRuleFeedId, setEditRuleFeedId] = useState<number | null>(null);
    const [editRuleCategoryId, setEditRuleCategoryId] = useState<number | null>(null);
    const [editRuleAsnInput, setEditRuleAsnInput] = useState('');
    const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([]);

    // Estado para adicionar múltiplas regras
    const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
    const [bulkRuleMode, setBulkRuleMode] = useState<'match' | 'category' | 'feed'>('match');
    const [bulkRuleAction, setBulkRuleAction] = useState<RuleAction>('block');
    const [bulkTargets, setBulkTargets] = useState('');
    const [bulkCategories, setBulkCategories] = useState<number[]>([]);
    const [bulkFeeds, setBulkFeeds] = useState<number[]>([]);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [lineMetrics, setLineMetrics] = React.useState({ lineHeight: 24, paddingTop: 8 });
    const [textareaScroll, setTextareaScroll] = React.useState(0);

    // Valores exibidos no cabeçalho (atualizados ao salvar)
    const [display, setDisplay] = useState({
        name: policy.name,
        enabled: policy.enabled,
        defaultAction: policy.defaultAction,
        tipo: (policy.tipo ?? 'firewall') as PolicyTipo,
        scope: policy.scope,
        applianceId: policy.applianceId,
        grupoIds: policy.grupoIds,
        redesIds: policy.redesIds ?? [],
        priority: policy.priority ?? 10,
    });

    // Edição inline de nome/estado/escopo da política
    const [isEditingHeader, setIsEditingHeader] = useState(false);
    const [editName, setEditName] = useState(policy.name);
    const [editEnabled, setEditEnabled] = useState(policy.enabled);
    const [editDefaultAction, setEditDefaultAction] = useState(policy.defaultAction);
    const [editTipo, setEditTipo] = useState<PolicyTipo>((policy.tipo ?? 'firewall') as PolicyTipo);
    const [editScope, setEditScope] = useState<PolicyScope>(policy.scope);
    const [editApplianceId, setEditApplianceId] = useState<string | null>(
        policy.applianceId != null ? String(policy.applianceId) : null
    );
    const [editGrupoIds, setEditGrupoIds] = useState<string[]>(
        (policy.grupoIds ?? []).map(String)
    );
    const [editRedeIds, setEditRedeIds] = useState<string[]>(
        (policy.redesIds ?? []).map(String)
    );
    const [editPriority, setEditPriority] = useState<string>(String(policy.priority ?? 10));

    const queryAppliances = useQuery(['appliance-box'], async () => {
        const res = await axios.get<{ result: ApplianceDTO[] }>('/api/appliance', { withCredentials: true });
        return res.data.result;
    });

    const queryGrupos = useQuery(
        ['grupo_dispositivo_all'],
        async () => {
            const res = await axios.get<{ result: DeviceGroupRefDTO[] }>(
                `/api/grupo_dispositivo`,
                { withCredentials: true }
            );
            return res.data.result;
        },
        { enabled: editScope === 'grupos' || display.scope === 'grupos' }
    );

    const appliances = queryAppliances.data ?? [];
    const applianceOptions = appliances.map((a) => ({ id: String(a.id), label: a.nome }));
    const grupoOptions = (queryGrupos.data ?? []).map((g) => {
        const appNome = appliances.find((a) => a.id === g.appliance_id)?.nome ?? 'Sem appliance';
        return { id: String(g.id), label: g.nome, group: appNome };
    });
    const lanOptions = appliances.flatMap((a) =>
        (a.redesLan ?? []).map((lan: NetworkLanDTO) => ({
            id: String(lan.id),
            label: lan.nome,
            group: a.nome,
        }))
    );

    const queryRules = useQuery({
        queryKey: ['fw_rules', policy.id],
        queryFn: async () =>
            axios.get<{ ok: boolean; result: RuleDTO[] }>(
                `/api/firewall/policies/${policy.id}/rules`,
                { withCredentials: true },
            ),
        refetchOnWindowFocus: false,
    });

    const queryFeeds = useQuery({
        queryKey: ['fw_feeds'],
        queryFn: async () => axios.get<ResponseFeeds>('/api/firewall/feeds', { withCredentials: true }),
        refetchOnWindowFocus: false,
    });

    const queryCategories = useQuery({
        queryKey: ['fw_categories'],
        queryFn: async () => axios.get<ResponseCategories>('/api/firewall/categories', { withCredentials: true }),
        refetchOnWindowFocus: false,
    });

    const queryAsns = useQuery({
        queryKey: ['fw_asns'],
        queryFn: async () => axios.get<ResponseAsns>('/api/firewall/asns', { withCredentials: true }),
        refetchOnWindowFocus: false,
    });

    // Buscas separadas para o seletor ASN de nova regra e edição
    const debouncedNewAsnInput = useDebounce(newRuleAsnInput, 300);
    const debouncedEditAsnInput = useDebounce(editRuleAsnInput, 300);

    const queryAsnCatalogNew = useQuery({
        queryKey: ['asn_catalog', debouncedNewAsnInput],
        queryFn: async () => axios.get<ResponseAsnCatalog>(
            `/api/firewall/asns/catalog?q=${encodeURIComponent(debouncedNewAsnInput)}`,
            { withCredentials: true },
        ),
        enabled: isAddOpen && newRule.type === 'asn',
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    });

    const queryAsnCatalogEdit = useQuery({
        queryKey: ['asn_catalog_edit', debouncedEditAsnInput],
        queryFn: async () => axios.get<ResponseAsnCatalog>(
            `/api/firewall/asns/catalog?q=${encodeURIComponent(debouncedEditAsnInput)}`,
            { withCredentials: true },
        ),
        enabled: !!editingRule && editRulePayload.type === 'asn',
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    });

    const asnCatalogOptionsNew = React.useMemo(
        () => (queryAsnCatalogNew.data?.data?.result ?? []).map((e) => ({
            id: String(e.asn),
            label: `AS${e.asn} — ${e.nome}${e.pais ? ` (${e.pais})` : ''}`,
        })),
        [queryAsnCatalogNew.data],
    );

    const asnCatalogOptionsEdit = React.useMemo(
        () => (queryAsnCatalogEdit.data?.data?.result ?? []).map((e) => ({
            id: String(e.asn),
            label: `AS${e.asn} — ${e.nome}${e.pais ? ` (${e.pais})` : ''}`,
        })),
        [queryAsnCatalogEdit.data],
    );

    const rules = React.useMemo(
        () => queryRules.data?.data?.result ?? [],
        [queryRules.data?.data?.result],
    );
    const feeds = React.useMemo(
        () => queryFeeds.data?.data?.result ?? [],
        [queryFeeds.data?.data?.result],
    );
    const feedOptions = React.useMemo(
        () => feeds.map((feed) => ({ id: String(feed.id), label: feed.name })),
        [feeds],
    );
    const feedNameById = React.useMemo(
        () => new Map(feeds.map((feed) => [feed.id, feed.name])),
        [feeds],
    );
    const apiCategories = React.useMemo(
        () => queryCategories.data?.data?.result ?? [],
        [queryCategories.data?.data?.result],
    );
    const categoryOptions = React.useMemo(
        () => apiCategories.map((cat) => ({
            id: String(cat.id),
            label: `${cat.name} (${cat.entries} ${cat.entries === 1 ? 'regra' : 'regras'})`,
        })),
        [apiCategories],
    );
    const categoryNameById = React.useMemo(
        () => new Map(apiCategories.map((cat) => [cat.id, cat.name])),
        [apiCategories],
    );
    const categoryEntriesById = React.useMemo(
        () => new Map(apiCategories.map((cat) => [cat.id, cat.entries])),
        [apiCategories],
    );

    // Cada entrada de domínio de uma categoria gera uma regra tls-host
    // individual no MikroTik (ver facility_firewall.go, case "category"):
    // acima deste limite o volume de regras pode degradar o desempenho do
    // router durante o sync.
    const CATEGORY_MIKROTIK_PERFORMANCE_WARNING_THRESHOLD = 3500;

    const applianceById = React.useMemo(
        () => new Map(appliances.map((a) => [a.id, a])),
        [appliances],
    );
    const targetsMikrotik = React.useMemo(() => {
        if (display.scope === 'appliance') {
            return applianceById.get(display.applianceId ?? -1)?.agent_type === 'mikrotik';
        }
        if (display.scope === 'redes') {
            const applianceIds = new Set(
                appliances
                    .filter((a) => (a.redesLan ?? []).some((lan) => lan.id != null && display.redesIds.includes(lan.id)))
                    .map((a) => a.id)
            );
            return Array.from(applianceIds).some((id) => applianceById.get(id)?.agent_type === 'mikrotik');
        }
        if (display.scope === 'grupos') {
            const grupoApplianceIds = new Set(
                (queryGrupos.data ?? [])
                    .filter((g) => display.grupoIds.includes(g.id))
                    .map((g) => g.appliance_id)
            );
            return Array.from(grupoApplianceIds).some((id) => applianceById.get(id)?.agent_type === 'mikrotik');
        }
        // scope === 'global': a política se aplica a todos os appliances
        return appliances.some((a) => a.agent_type === 'mikrotik');
    }, [display.scope, display.applianceId, display.redesIds, display.grupoIds, appliances, applianceById, queryGrupos.data]);

    const categoryPerformanceWarning = (categoryIds: number[]): string | null => {
        if (!targetsMikrotik || display.tipo !== 'firewall') return null;
        const selected = apiCategories.filter((c) => categoryIds.includes(c.id));
        const totalEntries = selected.reduce((sum, c) => sum + c.entries, 0);
        if (totalEntries <= CATEGORY_MIKROTIK_PERFORMANCE_WARNING_THRESHOLD) return null;
        return selected.length > 1
            ? `As categorias selecionadas somam ${totalEntries} regras. Em appliances MikroTik, esse volume pode causar uso excessivo de CPU e perda de desempenho.`
            : `Esta categoria possui ${totalEntries} regras. Em appliances MikroTik, esse volume pode causar uso excessivo de CPU e perda de desempenho.`;
    };
    // Aviso da política inteira, acima da lista: soma por regra, não por
    // categoria distinta — cada regra de categoria vira seu próprio bloco de
    // regras no router.
    const policyCategoryWarning = React.useMemo(() => {
        if (!targetsMikrotik || display.tipo !== 'firewall') return null;
        const categoryRules = rules.filter((r) => r.type === 'category' && r.categoryId);
        const totalEntries = categoryRules.reduce((sum, r) => sum + (categoryEntriesById.get(r.categoryId!) ?? 0), 0);
        if (totalEntries <= CATEGORY_MIKROTIK_PERFORMANCE_WARNING_THRESHOLD) return null;
        const qtd = totalEntries.toLocaleString('pt-BR');
        return categoryRules.length > 1
            ? `As categorias desta política somam ${qtd} regras. Em appliances MikroTik, esse volume pode causar uso excessivo de CPU e perda de desempenho.`
            : `A categoria desta política possui ${qtd} regras. Em appliances MikroTik, esse volume pode causar uso excessivo de CPU e perda de desempenho.`;
    }, [targetsMikrotik, display.tipo, rules, categoryEntriesById]);
    const asnById = React.useMemo(
        () => new Map((queryAsns.data?.data?.result ?? []).map((a) => [a.id, a])),
        [queryAsns.data],
    );
    const existingMatchTargets = React.useMemo(
        () => rules
            .filter(rule => rule.type !== 'category')
            .map(rule => rule.target),
        [rules],
    );

    const mutUpdatePolicy = useMutation(
        async (data: { name: string; enabled: boolean; defaultAction: string; tipo: PolicyTipo; scope: PolicyScope; applianceId: number | null; grupoIds: number[]; redesIds: number[]; priority: number }) =>
            axios.put(`/api/firewall/policies/${policy.id}`, data, { withCredentials: true }),
        {
            onSuccess: (_, vars) => {
                queryClient.invalidateQueries(['fw_policies']);
                toastSuccess('Política atualizada!');
                setDisplay({
                    name: vars.name,
                    enabled: vars.enabled,
                    defaultAction: vars.defaultAction as typeof display.defaultAction,
                    tipo: vars.tipo,
                    scope: vars.scope,
                    applianceId: vars.applianceId,
                    grupoIds: vars.grupoIds,
                    redesIds: vars.redesIds,
                    priority: vars.priority,
                });
                setIsEditingHeader(false);
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao atualizar política');
                toastError(message);
            },
        },
    );

    const mutAdd = useMutation(
        async (rule: RulePayload) =>
            axios.post(`/api/firewall/policies/${policy.id}/rules`, rule, { withCredentials: true }),
        {
            onSuccess: () => {
                queryRules.refetch();
                toastSuccess('Regra adicionada!');
                setIsAddOpen(false);
                setNewRule({ ...BLANK_RULE });
                setNewRuleFeedId(null);
                setNewRuleCategoryId(null);
                setNewRuleAsnInput('');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao adicionar regra');
                toastError(message);
            },
        },
    );

    const mutUpdateRule = useMutation(
        async ({ ruleId, payload }: { ruleId: number; payload: RulePayload }) =>
            axios.put(`/api/firewall/policies/${policy.id}/rules/${ruleId}`, payload, { withCredentials: true }),
        {
            onSuccess: () => {
                queryRules.refetch();
                toastSuccess('Regra atualizada!');
                setEditingRule(null);
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao atualizar regra');
                toastError(message);
            },
        },
    );

    const mutBulkAdd = useMutation(
        async (rulesToAdd: RulePayload[]) => {
            for (const rule of rulesToAdd) {
                await axios.post(`/api/firewall/policies/${policy.id}/rules`, rule, { withCredentials: true });
            }
        },
        {
            onSuccess: (_, rulesToAdd) => {
                queryRules.refetch();
                toastSuccess(`${rulesToAdd.length} regra(s) adicionada(s)!`);
                setIsBulkAddOpen(false);
                setBulkTargets('');
                setBulkCategories([]);
                setBulkFeeds([]);
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao adicionar regras');
                toastError(message);
            },
        },
    );

    const mutRemove = useMutation(
        async (ruleId: number) =>
            axios.delete(`/api/firewall/policies/${policy.id}/rules/${ruleId}`, { withCredentials: true }),
        {
            onSuccess: (_, ruleId) => {
                queryRules.refetch();
                setSelectedRuleIds(prev => prev.filter(id => id !== ruleId));
                toastSuccess('Regra removida!');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao remover regra');
                toastError(message);
            },
        },
    );

    const mutBulkRemove = useMutation(
        async (ruleIds: number[]) => {
            for (const ruleId of ruleIds) {
                await axios.delete(`/api/firewall/policies/${policy.id}/rules/${ruleId}`, { withCredentials: true });
            }
        },
        {
            onSuccess: (_, ruleIds) => {
                queryRules.refetch();
                setSelectedRuleIds([]);
                toastSuccess(`${ruleIds.length} regra(s) removida(s)!`);
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao remover regras selecionadas');
                toastError(message);
            },
        },
    );

    const buildSingleRulePayload = (): RulePayload | null => {
        if (newRule.type === 'feed') {
            if (!newRuleFeedId) {
                toastError('Selecione um feed');
                return null;
            }
            const feedName = feedNameById.get(newRuleFeedId) ?? `Feed #${newRuleFeedId}`;
            return {
                type: 'category',
                target: feedName,
                action: newRule.action,
                schedule: newRule.schedule,
                source: 'feed',
                feedId: newRuleFeedId,
            };
        }

        if (newRule.type === 'category') {
            if (!newRuleCategoryId) {
                toastError('Selecione uma categoria');
                return null;
            }
            const catName = categoryNameById.get(newRuleCategoryId) ?? `Categoria #${newRuleCategoryId}`;
            return { type: 'category', target: catName, action: newRule.action, schedule: newRule.schedule, categoryId: newRuleCategoryId };
        }

        if (newRule.type === 'asn') {
            const asnNum = parseInt(newRule.target.trim(), 10);
            if (isNaN(asnNum) || asnNum <= 0) {
                toastError('Informe um número de ASN válido (ex: 13335)');
                return null;
            }
            return { type: 'asn', target: String(asnNum), action: newRule.action, schedule: newRule.schedule };
        }

        if (newRule.type === 'https') {
            const target = newRule.target.trim();
            if (!target) return null;
            const validation = validateMatchTarget(target);
            if (!validation.ok) {
                toastError(validation.message);
                return null;
            }
            return { type: 'https', target, action: newRule.action, schedule: newRule.schedule };
        }

        const target = newRule.target.trim();
        if (!target) return null;

        const validation = validateMatchTarget(target);
        if (!validation.ok) {
            toastError(validation.message);
            return null;
        }

        return { type: 'match', target, action: newRule.action, schedule: newRule.schedule };
    };

    const buildEditRulePayload = (): RulePayload | null => {
        if (editRulePayload.type === 'feed') {
            if (!editRuleFeedId) {
                toastError('Selecione um feed');
                return null;
            }
            const feedName = feedNameById.get(editRuleFeedId) ?? `Feed #${editRuleFeedId}`;
            return {
                type: 'category',
                target: feedName,
                action: editRulePayload.action,
                schedule: editRulePayload.schedule,
                source: 'feed',
                feedId: editRuleFeedId,
            };
        }

        if (editRulePayload.type === 'category') {
            if (!editRuleCategoryId) {
                toastError('Selecione uma categoria');
                return null;
            }
            const catName = categoryNameById.get(editRuleCategoryId) ?? `Categoria #${editRuleCategoryId}`;
            return { type: 'category', target: catName, action: editRulePayload.action, schedule: editRulePayload.schedule, categoryId: editRuleCategoryId };
        }

        if (editRulePayload.type === 'asn') {
            const asnNum = parseInt(editRulePayload.target.trim(), 10);
            if (isNaN(asnNum) || asnNum <= 0) {
                toastError('Informe um número de ASN válido (ex: 13335)');
                return null;
            }
            return { type: 'asn', target: String(asnNum), action: editRulePayload.action, schedule: editRulePayload.schedule };
        }

        if (editRulePayload.type === 'https') {
            const target = editRulePayload.target.trim();
            if (!target) return null;
            const validation = validateMatchTarget(target);
            if (!validation.ok) {
                toastError(validation.message);
                return null;
            }
            return { type: 'https', target, action: editRulePayload.action, schedule: editRulePayload.schedule };
        }

        const target = editRulePayload.target.trim();
        if (!target) return null;

        const validation = validateMatchTarget(target);
        if (!validation.ok) {
            toastError(validation.message);
            return null;
        }

        return { type: 'match', target, action: editRulePayload.action, schedule: editRulePayload.schedule };
    };

    const openEditRule = (rule: RuleDTO) => {
        let type: RuleType;
        if (rule.source === 'feed') {
            type = 'feed';
        } else if (rule.type === 'category') {
            type = 'category';
        } else if (rule.type === 'asn') {
            type = 'asn';
        } else if (rule.type === 'https') {
            type = 'https';
        } else {
            type = inferRuleTypeFromTarget(rule.target);
        }
        setEditRulePayload({
            type,
            target: rule.target,
            action: rule.action,
            schedule: rule.schedule ?? '',
            source: rule.source ?? 'manual',
            feedId: rule.feedId ?? null,
            categoryId: rule.categoryId ?? null,
        });
        setEditRuleFeedId(rule.source === 'feed' && rule.feedId ? rule.feedId : null);
        setEditRuleCategoryId(rule.type === 'category' && rule.categoryId ? rule.categoryId : null);
        setEditRuleAsnInput(rule.type === 'asn' ? rule.target : '');
        setEditingRule(rule);
    };

    const handleBulkAdd = () => {
        const rulesToAdd: RulePayload[] = [];

        if (bulkRuleMode === 'category') {
            for (const catId of bulkCategories) {
                const catName = categoryNameById.get(catId) ?? `Categoria #${catId}`;
                rulesToAdd.push({ type: 'category', target: catName, action: bulkRuleAction, schedule: '', categoryId: catId });
            }
        } else if (bulkRuleMode === 'feed') {
            for (const feedId of bulkFeeds) {
                const feedName = feedNameById.get(feedId) ?? `Feed #${feedId}`;
                rulesToAdd.push({ type: 'category', target: feedName, action: bulkRuleAction, schedule: '', source: 'feed', feedId });
            }
        } else {
            const analyzed = analyzeBulkTargets(bulkTargets, existingMatchTargets);
            const invalidLines = analyzed.filter(line => line.status === 'invalid');

            if (invalidLines.length > 0) {
                toastError('Existem linhas inválidas. Corrija antes de continuar.');
                return;
            }

            const uniqueTargets = analyzed
                .filter(line => line.status === 'ok')
                .map(line => line.target);

            for (const target of uniqueTargets) {
                rulesToAdd.push({
                    type: 'match',
                    target,
                    action: bulkRuleAction,
                    schedule: '',
                });
            }
        }

        if (rulesToAdd.length === 0) {
            toastError('Adicione pelo menos um alvo, categoria ou feed');
            return;
        }

        mutBulkAdd.mutate(rulesToAdd);
    };

    const bulkLineAnalysis = React.useMemo(
        () => analyzeBulkTargets(bulkTargets, existingMatchTargets),
        [bulkTargets, existingMatchTargets],
    );
    const bulkTargetLines = React.useMemo(() => bulkTargets.split('\n'), [bulkTargets]);
    const analysisMap = React.useMemo(() => {
        const map = new Map<number, typeof bulkLineAnalysis[0]>();
        bulkLineAnalysis.forEach(analysis => {
            map.set(analysis.lineNumber, analysis);
        });
        return map;
    }, [bulkLineAnalysis]);
    const bulkValidLineCount = bulkLineAnalysis.filter(line => line.status === 'ok').length;
    const bulkInvalidLineCount = bulkLineAnalysis.filter(line => line.status === 'invalid').length;
    const bulkDuplicateLineCount = bulkLineAnalysis.filter(line => line.status === 'duplicate').length;
    const bulkRedundantLineCount = bulkLineAnalysis.filter(line => line.status === 'redundant').length;
    const allRuleIds = React.useMemo(() => rules.map(rule => rule.id), [rules]);
    const hasSelection = selectedRuleIds.length > 0;
    const allSelected = allRuleIds.length > 0 && allRuleIds.every(id => selectedRuleIds.includes(id));

    const toggleRuleSelection = (ruleId: number) => {
        setSelectedRuleIds(prev => prev.includes(ruleId)
            ? prev.filter(id => id !== ruleId)
            : [...prev, ruleId]);
    };

    const toggleAllSelection = () => {
        if (allSelected) {
            setSelectedRuleIds([]);
            return;
        }
        setSelectedRuleIds(allRuleIds);
    };

    React.useEffect(() => {
        const valid = new Set(allRuleIds);
        setSelectedRuleIds(prev => prev.filter(id => valid.has(id)));
    }, [allRuleIds]);

    React.useEffect(() => {
        if (isBulkAddOpen && textareaRef.current) {
            setTimeout(() => {
                if (!textareaRef.current) return;

                const textarea = textareaRef.current;
                const computed = window.getComputedStyle(textarea);

                const paddingTop = parseFloat(computed.paddingTop);
                const borderTop = parseFloat(computed.borderTopWidth);

                // Obter altura do label
                const wrapper = textarea.parentElement;
                const label = wrapper?.querySelector('label');
                const labelHeight = label ? label.offsetHeight : 0;

                // Criar elemento test invisível para medir altura de uma linha
                const testSpan = document.createElement('span');
                testSpan.style.cssText = `
                    font-family: ${computed.fontFamily};
                    font-size: ${computed.fontSize};
                    line-height: ${computed.lineHeight};
                    position: absolute;
                    visibility: hidden;
                    white-space: pre;
                `;
                testSpan.textContent = 'X';
                document.body.appendChild(testSpan);

                const lineHeight = testSpan.offsetHeight;
                document.body.removeChild(testSpan);

                // Offset total = label + padding + border
                const topOffset = labelHeight + paddingTop + borderTop;

                console.log('📏 Alinhamento:', { lineHeight, topOffset, labelHeight, paddingTop, borderTop });

                setLineMetrics({
                    lineHeight,
                    paddingTop: topOffset
                });
            }, 100);
        }
    }, [isBulkAddOpen, bulkTargets]);

    const headers: DataTableHeader[] = [
        {
            label: 'Sel.',
            width: '42px',
            alignCenter: true,
            render: (row: RuleDTO) => (
                <input
                    type="checkbox"
                    checked={selectedRuleIds.includes(row.id)}
                    onChange={() => toggleRuleSelection(row.id)}
                    aria-label={`Selecionar regra ${row.id}`}
                />
            ),
        },
        {
            label: '#',
            width: '50px',
            alignCenter: true,
            render: (row: RuleDTO) => <span style={{ color: 'var(--tc-color-muted)' }}>{row.order}</span>,
        },
        {
            label: 'Tipo',
            width: '110px',
            render: (row: RuleDTO) => (
                <Badge variant="secondary">
                    {RULE_TYPE_OPTIONS.find(o => o.id === row.type)?.label ?? row.type}
                </Badge>
            ),
        },
        {
            label: 'Alvo',
            width: '35%',
            render: (row: RuleDTO) => {
                if (row.type === 'asn' && row.asnId) {
                    const asn = asnById.get(row.asnId);
                    if (asn) {
                        const isPlaceholder = asn.name === `AS${asn.asn}`;
                        return <span>
                            AS{asn.asn}
                            {!isPlaceholder && ` — ${asn.name}`}
                            {asn.country ? ` (${asn.country})` : ''}
                        </span>;
                    }
                }
                if (row.type === 'category' && row.categoryId) {
                    const entries = categoryEntriesById.get(row.categoryId);
                    return <span>
                        {row.target}
                        {entries != null && (
                            <span style={{ color: 'var(--tc-color-muted)', marginLeft: '6px', fontSize: '0.9em' }}>
                                ({entries.toLocaleString('pt-BR')} {entries === 1 ? 'regra' : 'regras'})
                            </span>
                        )}
                    </span>;
                }
                return <span>{row.target}</span>;
            },
        },
        {
            label: 'Ação',
            width: '100px',
            render: (row: RuleDTO) => (
                <Badge variant={ACTION_VARIANTS[row.action]}>
                    {row.action === 'allow' ? 'Permitir' : 'Bloquear'}
                </Badge>
            ),
        },
        {
            label: 'Origem',
            width: '170px',
            render: (row: RuleDTO) => (
                row.source === 'feed'
                    ? <Badge variant="warning">Feed: {row.feedId ? (feedNameById.get(row.feedId) ?? `#${row.feedId}`) : 'não definido'}</Badge>
                    : row.type === 'category' && row.categoryId
                        ? <Badge variant="info">Cat: {categoryNameById.get(row.categoryId) ?? `#${row.categoryId}`}</Badge>
                        : row.type === 'asn'
                            ? <Badge variant="info">ASN</Badge>
                            : <Badge variant="secondary">Manual</Badge>
            ),
        },
        {
            label: 'Agendamento',
            render: (row: RuleDTO) => row.schedule || <span style={{ color: 'var(--tc-color-muted)' }}>—</span>,
        },
        {
            label: 'Ações',
            width: '110px',
            stickRight: true,
            alignRight: true,
            render: (row: RuleDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="outline-primary"
                        icon={mdiPencil}
                        onClick={() => openEditRule(row)}
                        title="Editar regra"
                    />
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        icon={mdiTrashCan}
                        onConfirm={() => mutRemove.mutate(row.id)}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <>
            <Modal
                show
                title={`Política — ${display.name}`}
                onClose={onClose}
                onCancel={onClose}
                hideControls
                size="lg"
                instanceId="policy-editor"
            >
                <VBox style={{ gap: '16px' }}>
                    {/* Cabeçalho editável */}
                    <div style={{
                        border: '1px solid var(--tc-color-gray-300)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        background: 'var(--tc-color-gray-50)',
                    }}>
                        {!isEditingHeader ? (
                            <HBox style={{ alignItems: 'center', gap: '12px' }}>
                                <VBox style={{ flex: 1, gap: '4px' }}>
                                    <span style={{ fontWeight: 600 }}>{display.name}</span>
                                    <HBox style={{ gap: '8px' }}>
                                        <Badge variant={display.enabled ? 'success' : 'secondary'}>
                                            {display.enabled ? 'Ativa' : 'Inativa'}
                                        </Badge>
                                        <Badge variant={display.defaultAction === 'allow' ? 'success' : 'danger'}>
                                            Padrão: {display.defaultAction === 'allow' ? 'Permitir' : 'Negar'}
                                        </Badge>
                                        <Badge variant="secondary">
                                            {display.scope === 'global' && 'Global'}
                                            {display.scope === 'appliance' && `Appliance: ${appliances.find(a => a.id === display.applianceId)?.nome ?? display.applianceId}`}
                                            {display.scope === 'grupos' && `Grupos: ${grupoOptions.length ? display.grupoIds.map(gid => grupoOptions.find(go => go.id === String(gid))?.label ?? gid).join(', ') : `(${display.grupoIds.length})`}`}
                                            {display.scope === 'redes' && `Redes: ${lanOptions.length ? display.redesIds.map(rid => lanOptions.find(lo => lo.id === String(rid))?.label ?? rid).join(', ') : `(${display.redesIds.length})`}`}
                                        </Badge>
                                        <Badge variant={display.tipo === 'proxy' ? 'warning' : 'secondary'}>
                                            {display.tipo === 'proxy' ? 'Proxy' : 'Firewall IP'}
                                        </Badge>
                                        <Badge variant="info">
                                            Prioridade: {display.priority}
                                        </Badge>
                                    </HBox>
                                    {display.tipo === 'proxy' && (
                                        <div style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--tc-background-color-warning-subtle)', border: '1px solid var(--tc-border-color-warning-subtle)', borderRadius: '6px', fontSize: '13px', color: 'var(--tc-text-color-warning-emphasis)' }}>
                                            <Icon path={mdiAlert} size={0.6} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                            <strong>Política Proxy:</strong> As regras desta política são aplicadas no Squid via SSH. Os navegadores dos usuários devem estar configurados para usar o proxy (IP:porta). Sem essa configuração, as regras não terão efeito.
                                        </div>
                                    )}
                                </VBox>
                                <Botao
                                    size="sm"
                                    variant="outline-primary"
                                    icon={mdiPencil}
                                    onClick={() => {
                                        setEditName(display.name);
                                        setEditEnabled(display.enabled);
                                        setEditDefaultAction(display.defaultAction);
                                        setEditTipo(display.tipo);
                                        setEditScope(display.scope);
                                        setEditApplianceId(display.applianceId != null ? String(display.applianceId) : null);
                                        setEditGrupoIds((display.grupoIds ?? []).map(String));
                                        setEditRedeIds((display.redesIds ?? []).map(String));
                                        setEditPriority(String(display.priority));
                                        setIsEditingHeader(true);
                                    }}
                                >
                                    Editar
                                </Botao>
                            </HBox>
                        ) : (
                            <VBox style={{ gap: '12px' }}>
                                <HBox style={{ gap: '12px' }}>
                                    <VBox style={{ flex: 2 }}>
                                        <CampoTexto
                                            label="Nome da política"
                                            value={editName}
                                            onChange={setEditName}
                                            autoFocus
                                        />
                                    </VBox>
                                    <VBox style={{ flex: 1, maxWidth: '120px' }}>
                                        <CampoTexto
                                            label="Prioridade"
                                            value={editPriority}
                                            onChange={setEditPriority}
                                            type="number"
                                        />
                                    </VBox>
                                    <VBox style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Mecanismo de enforcement"
                                            noSearch
                                            options={[
                                                { id: 'firewall', label: 'Firewall IP' },
                                                { id: 'proxy', label: 'Proxy' },
                                            ]}
                                            value={editTipo}
                                            onChange={v => setEditTipo(v as PolicyTipo)}
                                        />
                                    </VBox>
                                    <VBox style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Ação padrão"
                                            noSearch
                                            options={DEFAULT_ACTION_OPTIONS}
                                            value={editDefaultAction}
                                            onChange={v => setEditDefaultAction(v as typeof editDefaultAction)}
                                        />
                                    </VBox>
                                </HBox>
                                <HBox style={{ gap: '12px' }}>
                                    <VBox style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Escopo"
                                            noSearch
                                            value={editScope}
                                            onChange={(v) => { setEditScope(v as PolicyScope); setEditApplianceId(null); setEditGrupoIds([]); setEditRedeIds([]); }}
                                            options={[
                                                { id: 'global', label: 'Global' },
                                                { id: 'appliance', label: 'Appliance específico' },
                                                { id: 'grupos', label: 'Grupos de dispositivos' },
                                                { id: 'redes', label: 'Rede LAN' },
                                            ]}
                                        />
                                    </VBox>
                                    {editScope === 'appliance' && (
                                        <VBox style={{ flex: 1 }}>
                                            <CampoSeletor
                                                label="Appliance"
                                                value={editApplianceId}
                                                onChange={setEditApplianceId}
                                                options={applianceOptions}
                                            />
                                        </VBox>
                                    )}
                                </HBox>
                                {editScope === 'grupos' && (
                                    <CampoMultiSeletor
                                        label="Grupos de dispositivos"
                                        value={editGrupoIds}
                                        onChange={setEditGrupoIds}
                                        options={grupoOptions}
                                    />
                                )}
                                {editScope === 'redes' && (
                                    <CampoMultiSeletor
                                        label="Redes LAN"
                                        value={editRedeIds}
                                        onChange={setEditRedeIds}
                                        options={lanOptions}
                                    />
                                )}
                                <HBox style={{ gap: '8px', alignItems: 'center' }}>
                                    <Botao
                                        size="sm"
                                        variant="none-primary"
                                        icon={editEnabled ? mdiToggleSwitch : mdiToggleSwitchOffOutline}
                                        iconProps={{ color: editEnabled ? 'var(--tc-color-success)' : 'var(--tc-color-muted)' }}
                                        onClick={() => setEditEnabled(v => !v)}
                                    >
                                        {editEnabled ? 'Ativa' : 'Inativa'}
                                    </Botao>
                                    <div style={{ flex: 1 }} />
                                    <Botao
                                        size="sm"
                                        variant="outline-secondary"
                                        icon={mdiClose}
                                        onClick={() => setIsEditingHeader(false)}
                                    >
                                        Cancelar
                                    </Botao>
                                    <Botao
                                        size="sm"
                                        variant="primary"
                                        icon={mutUpdatePolicy.isLoading ? mdiLoading : mdiCheck}
                                        iconProps={{ spin: mutUpdatePolicy.isLoading }}
                                        disabled={mutUpdatePolicy.isLoading || !editName.trim() || (editScope === 'appliance' && !editApplianceId) || (editScope === 'redes' && editRedeIds.length === 0)}
                                        onClick={() => mutUpdatePolicy.mutate({
                                            name: editName.trim(),
                                            enabled: editEnabled,
                                            defaultAction: editDefaultAction,
                                            tipo: editTipo,
                                            scope: editScope,
                                            applianceId: editApplianceId ? Number(editApplianceId) : null,
                                            grupoIds: editGrupoIds.map(Number),
                                            redesIds: editRedeIds.map(Number),
                                            priority: Number(editPriority) || 10,
                                        })}
                                    >
                                        Salvar
                                    </Botao>
                                </HBox>
                            </VBox>
                        )}
                    </div>

                    {/* Barra de ações da tabela */}
                    <HBox style={{ justifyContent: 'space-between', gap: '8px' }}>
                        <HBox style={{ gap: '8px' }}>
                            <Botao
                                size="sm"
                                variant="outline-secondary"
                                onClick={toggleAllSelection}
                            >
                                {allSelected ? 'Limpar seleção' : 'Selecionar todas'}
                            </Botao>
                            <BotaoPerigoso
                                size="sm"
                                variant="outline-danger"
                                icon={mdiTrashCan}
                                disabled={!hasSelection || mutBulkRemove.isLoading}
                                onConfirm={() => mutBulkRemove.mutate(selectedRuleIds)}
                                confirmation={`Tem certeza que deseja excluir ${selectedRuleIds.length} regra(s)?`}
                            >
                                Excluir selecionadas
                            </BotaoPerigoso>
                        </HBox>
                        <Botao
                            size="sm"
                            variant="outline-primary"
                            icon={mdiPlus}
                            onClick={() => setIsBulkAddOpen(true)}
                        >
                            Adicionar múltiplas regras
                        </Botao>
                        <Botao
                            size="sm"
                            variant="primary"
                            icon={mdiPlus}
                            onClick={() => setIsAddOpen(true)}
                        >
                            Adicionar regra
                        </Botao>
                    </HBox>

                    <CategoryPerformanceWarning message={policyCategoryWarning} />

                    {queryRules.isLoading && (
                        <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                            <Icon path={mdiLoading} spin size={1.5} />
                        </HBox>
                    )}

                    {!queryRules.isLoading && (
                        <DataTable
                            cabecalhos={headers}
                            linhas={rules}
                            rowSize="sm"
                        />
                    )}

                    {!queryRules.isLoading && rules.length === 0 && (
                        <p style={{ textAlign: 'center', color: 'var(--tc-color-muted)', padding: '24px' }}>
                            Nenhuma regra cadastrada. Adicione a primeira regra acima.
                        </p>
                    )}
                </VBox>
            </Modal>

            {/* Modal aninhado para adicionar regra */}
            <Modal
                show={isAddOpen}
                title="Nova Regra"
                onClose={() => setIsAddOpen(false)}
                onCancel={() => setIsAddOpen(false)}
                onConfirm={() => {
                    const payload = buildSingleRulePayload();
                    if (payload) mutAdd.mutate(payload);
                }}
                confirmProps={{
                    label: 'Adicionar',
                    disabled: mutAdd.isLoading || (newRule.type === 'feed' ? !newRuleFeedId : newRule.type === 'category' ? !newRuleCategoryId : !newRule.target.trim()),
                }}
                cancelProps={{ label: 'Cancelar' }}
                size="sm"
                instanceId="add-rule"
            >
                <VBox style={{ gap: '12px' }}>
                    <HBox style={{ gap: '12px' }}>
                        <VBox style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Tipo"
                                noSearch
                                options={RULE_TYPE_OPTIONS.filter(o => (o.id !== 'asn' || display.tipo !== 'proxy') && (o.id !== 'https' || display.tipo !== 'proxy'))}
                                value={newRule.type}
                                onChange={v => {
                                    const newType = v as RuleType;
                                    const keepTarget = FREE_TEXT_RULE_TYPES.includes(newRule.type) && FREE_TEXT_RULE_TYPES.includes(newType);
                                    setNewRule(r => ({ ...r, type: newType, target: keepTarget ? r.target : '' }));
                                    setNewRuleFeedId(null);
                                    setNewRuleCategoryId(null);
                                    setNewRuleAsnInput('');
                                }}
                            />
                        </VBox>
                        <VBox style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Ação"
                                noSearch
                                options={RULE_ACTION_OPTIONS}
                                value={newRule.action}
                                onChange={v => setNewRule(r => ({ ...r, action: v as RuleAction }))}
                            />
                        </VBox>
                    </HBox>
                    {newRule.type === 'category' ? (
                        <>
                            <CampoSeletor
                                label="Categoria"
                                options={categoryOptions}
                                value={newRuleCategoryId ? String(newRuleCategoryId) : null}
                                onChange={v => setNewRuleCategoryId(v ? Number(v) : null)}
                                placeholder={categoryOptions.length > 0 ? 'Selecione uma categoria...' : 'Nenhuma categoria disponível'}
                            />
                            <CategoryPerformanceWarning message={categoryPerformanceWarning(newRuleCategoryId ? [newRuleCategoryId] : [])} />
                        </>
                    ) : newRule.type === 'feed' ? (
                        <CampoSeletor
                            label="Feed de IP"
                            noSearch
                            options={feedOptions}
                            value={newRuleFeedId ? String(newRuleFeedId) : null}
                            onChange={v => setNewRuleFeedId(v ? Number(v) : null)}
                            placeholder={feedOptions.length > 0 ? 'Selecione um feed...' : 'Nenhum feed disponível'}
                        />
                    ) : newRule.type === 'asn' ? (
                        <CampoSeletorAsync
                            label="ASN"
                            placeholder="Digite o nome ou número do ASN..."
                            inputValue={newRuleAsnInput}
                            onInputChange={setNewRuleAsnInput}
                            selectedId={newRule.target || null}
                            onSelect={(id, label) => {
                                setNewRule(r => ({ ...r, target: id }));
                                setNewRuleAsnInput(label);
                            }}
                            onClear={() => {
                                setNewRule(r => ({ ...r, target: '' }));
                                setNewRuleAsnInput('');
                            }}
                            options={asnCatalogOptionsNew}
                            loading={queryAsnCatalogNew.isFetching}
                        />
                    ) : (
                        <CampoTexto
                            label="Alvo"
                            value={newRule.target}
                            onChange={v => setNewRule(r => ({ ...r, target: v }))}
                            placeholder="Ex: dominio.com:443, 192.168.1.0/24:53, :443"
                            autoFocus
                        />
                    )}
                    <CampoTexto
                        label="Agendamento (opcional)"
                        value={newRule.schedule}
                        onChange={v => setNewRule(r => ({ ...r, schedule: v }))}
                        placeholder="Ex: Seg-Sex 08:00-18:00"
                    />
                </VBox>
            </Modal>

            {/* Modal aninhado para editar regra */}
            <Modal
                show={!!editingRule}
                title="Editar Regra"
                onClose={() => setEditingRule(null)}
                onCancel={() => setEditingRule(null)}
                onConfirm={() => {
                    if (!editingRule) return;
                    const payload = buildEditRulePayload();
                    if (payload) {
                        mutUpdateRule.mutate({ ruleId: editingRule.id, payload });
                    }
                }}
                confirmProps={{
                    label: 'Salvar',
                    disabled: mutUpdateRule.isLoading || (editRulePayload.type === 'feed' ? !editRuleFeedId : editRulePayload.type === 'category' ? !editRuleCategoryId : !editRulePayload.target.trim()),
                }}
                cancelProps={{ label: 'Cancelar' }}
                size="sm"
                instanceId="edit-rule"
            >
                <VBox style={{ gap: '12px' }}>
                    <HBox style={{ gap: '12px' }}>
                        <VBox style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Tipo"
                                noSearch
                                options={RULE_TYPE_OPTIONS.filter(o => (o.id !== 'asn' || display.tipo !== 'proxy') && (o.id !== 'https' || display.tipo !== 'proxy'))}
                                value={editRulePayload.type}
                                onChange={v => {
                                    const newType = v as RuleType;
                                    const keepTarget = FREE_TEXT_RULE_TYPES.includes(editRulePayload.type) && FREE_TEXT_RULE_TYPES.includes(newType);
                                    setEditRulePayload(r => ({ ...r, type: newType, target: keepTarget ? r.target : '' }));
                                    setEditRuleFeedId(null);
                                    setEditRuleCategoryId(null);
                                    setEditRuleAsnInput('');
                                }}
                            />
                        </VBox>
                        <VBox style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Ação"
                                noSearch
                                options={RULE_ACTION_OPTIONS}
                                value={editRulePayload.action}
                                onChange={v => setEditRulePayload(r => ({ ...r, action: v as RuleAction }))}
                            />
                        </VBox>
                    </HBox>
                    {editRulePayload.type === 'category' ? (
                        <>
                            <CampoSeletor
                                label="Categoria"
                                options={categoryOptions}
                                value={editRuleCategoryId ? String(editRuleCategoryId) : null}
                                onChange={v => setEditRuleCategoryId(v ? Number(v) : null)}
                                placeholder={categoryOptions.length > 0 ? 'Selecione uma categoria...' : 'Nenhuma categoria disponível'}
                            />
                            <CategoryPerformanceWarning message={categoryPerformanceWarning(editRuleCategoryId ? [editRuleCategoryId] : [])} />
                        </>
                    ) : editRulePayload.type === 'feed' ? (
                        <CampoSeletor
                            label="Feed de IP"
                            noSearch
                            options={feedOptions}
                            value={editRuleFeedId ? String(editRuleFeedId) : null}
                            onChange={v => setEditRuleFeedId(v ? Number(v) : null)}
                            placeholder={feedOptions.length > 0 ? 'Selecione um feed...' : 'Nenhum feed disponível'}
                        />
                    ) : editRulePayload.type === 'asn' ? (
                        <CampoSeletorAsync
                            label="ASN"
                            placeholder="Digite o nome ou número do ASN..."
                            inputValue={editRuleAsnInput}
                            onInputChange={setEditRuleAsnInput}
                            selectedId={editRulePayload.target || null}
                            onSelect={(id, label) => {
                                setEditRulePayload(r => ({ ...r, target: id }));
                                setEditRuleAsnInput(label);
                            }}
                            onClear={() => {
                                setEditRulePayload(r => ({ ...r, target: '' }));
                                setEditRuleAsnInput('');
                            }}
                            options={asnCatalogOptionsEdit}
                            loading={queryAsnCatalogEdit.isFetching}
                        />
                    ) : (
                        <CampoTexto
                            label="Alvo"
                            value={editRulePayload.target}
                            onChange={v => setEditRulePayload(r => ({ ...r, target: v }))}
                            placeholder="Ex: dominio.com:443, 192.168.1.0/24:53, :443"
                            autoFocus
                        />
                    )}
                    <CampoTexto
                        label="Agendamento (opcional)"
                        value={editRulePayload.schedule}
                        onChange={v => setEditRulePayload(r => ({ ...r, schedule: v }))}
                        placeholder="Ex: Seg-Sex 08:00-18:00"
                    />
                </VBox>
            </Modal>

            {/* Modal aninhado para adicionar múltiplas regras */}
            <Modal
                show={isBulkAddOpen}
                title="Adicionar Múltiplas Regras"
                onClose={() => setIsBulkAddOpen(false)}
                onCancel={() => setIsBulkAddOpen(false)}
                onConfirm={handleBulkAdd}
                confirmProps={{
                    label: 'Adicionar Todas',
                    disabled: mutBulkAdd.isLoading || (
                        bulkRuleMode === 'category' ? bulkCategories.length === 0 :
                            bulkRuleMode === 'feed' ? bulkFeeds.length === 0 :
                                bulkValidLineCount === 0 || bulkInvalidLineCount > 0
                    )
                }}
                cancelProps={{ label: 'Cancelar' }}
                size="md"
                instanceId="bulk-add-rule"
            >
                <VBox style={{ gap: '12px' }}>
                    <HBox style={{ gap: '12px' }}>
                        <VBox style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Tipo"
                                noSearch
                                options={BULK_MODE_OPTIONS}
                                value={bulkRuleMode}
                                onChange={v => {
                                    setBulkRuleMode(v as 'match' | 'category' | 'feed');
                                    setBulkTargets('');
                                    setBulkCategories([]);
                                    setBulkFeeds([]);
                                }}
                            />
                        </VBox>
                        <VBox style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Ação"
                                noSearch
                                options={RULE_ACTION_OPTIONS}
                                value={bulkRuleAction}
                                onChange={v => setBulkRuleAction(v as RuleAction)}
                            />
                        </VBox>
                    </HBox>

                    {bulkRuleMode === 'category' ? (
                        <>
                            <CampoMultiSeletor
                                label="Categorias"
                                options={categoryOptions}
                                value={bulkCategories.map(String)}
                                onChange={v => setBulkCategories((v ?? []).map(Number))}
                                placeholder={categoryOptions.length > 0 ? 'Selecione uma ou mais categorias...' : 'Nenhuma categoria disponível'}
                            />
                            <CategoryPerformanceWarning message={categoryPerformanceWarning(bulkCategories)} />
                        </>
                    ) : bulkRuleMode === 'feed' ? (
                        <CampoMultiSeletor
                            label="Feed(s) de IP"
                            options={feedOptions}
                            value={bulkFeeds.map(String)}
                            onChange={v => setBulkFeeds((v ?? []).map(Number))}
                            placeholder={feedOptions.length > 0 ? 'Selecione um ou mais feeds...' : 'Nenhum feed disponível'}
                        />
                    ) : (
                        <VBox style={{ gap: '8px' }}>
                            {bulkLineAnalysis.length > 0 && (
                                <HBox style={{ gap: '8px', fontSize: '11px', color: 'var(--tc-color-muted)' }}>
                                    <span style={{ color: 'var(--tc-color-success)' }}>● OK</span>
                                    <span style={{ color: 'var(--tc-color-warning)' }}>● Duplicado</span>
                                    <span style={{ color: 'var(--tc-color-info)' }}>● Redundante</span>
                                    <span style={{ color: 'var(--tc-color-danger)' }}>● Inválido</span>
                                </HBox>
                            )}
                            <div style={{ position: 'relative', overflow: 'hidden' }}>
                                {bulkLineAnalysis.length > 0 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: '0',
                                            top: `${lineMetrics.paddingTop}px`,
                                            width: '4px',
                                            pointerEvents: 'none',
                                            zIndex: 1,
                                            overflow: 'hidden',
                                            height: `${lineMetrics.lineHeight * bulkTargetLines.length}px`,
                                        }}
                                    >
                                        <div
                                            style={{
                                                transform: `translateY(-${textareaScroll}px)`,
                                            }}
                                        >
                                            {bulkTargetLines.map((_, lineIdx) => {
                                                const analysis = analysisMap.get(lineIdx + 1);
                                                if (!analysis) {
                                                    return (
                                                        <div
                                                            key={`empty-${lineIdx}`}
                                                            style={{
                                                                height: `${lineMetrics.lineHeight}px`,
                                                                marginBottom: '0',
                                                            }}
                                                        />
                                                    );
                                                }

                                                const bg = analysis.status === 'ok'
                                                    ? 'var(--tc-color-success)'
                                                    : analysis.status === 'duplicate'
                                                        ? 'var(--tc-color-warning)'
                                                        : analysis.status === 'redundant'
                                                            ? 'var(--tc-color-info)'
                                                            : 'var(--tc-color-danger)';

                                                return (
                                                    <div
                                                        key={`${analysis.lineNumber}-${analysis.target}`}
                                                        style={{
                                                            height: `${lineMetrics.lineHeight}px`,
                                                            background: bg,
                                                            marginBottom: '0',
                                                            borderRadius: '2px',
                                                        }}
                                                        title={`Linha ${analysis.lineNumber}: ${analysis.message}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                <div style={{ padding: '4px', paddingTop: '0', paddingLeft: bulkLineAnalysis.length > 0 ? '12px' : '4px' }}>
                                    <CampoMemo
                                        ref={textareaRef}
                                        label="Alvos (um por linha)"
                                        value={bulkTargets}
                                        onChange={v => setBulkTargets(v)}
                                        onScroll={(e) => setTextareaScroll(e.currentTarget.scrollTop)}
                                        placeholder={'Ex:\nfacebook.com:443\n192.168.1.0/24:53/udp\n:8080'}
                                        rows={8}
                                        tooltipText="Formatos aceitos por linha: domínio:porta, ip/cidr:porta ou :porta. Porta aceita /tcp ou /udp (padrão tcp)."
                                        autoFocus
                                    />
                                </div>
                            </div>
                            {bulkTargets.trim() && (
                                <div style={{
                                    padding: '8px 12px',
                                    background: 'var(--tc-color-gray-50)',
                                    borderRadius: '4px',
                                    fontSize: '0.9em',
                                    color: 'var(--tc-color-muted)',
                                    textAlign: 'center'
                                }}>
                                    <strong>{bulkValidLineCount}</strong> válida(s)
                                    {bulkDuplicateLineCount > 0 && (
                                        <>, <strong style={{ color: 'var(--tc-color-warning)' }}>{bulkDuplicateLineCount}</strong> duplicada(s)</>
                                    )}
                                    {bulkRedundantLineCount > 0 && (
                                        <>, <strong style={{ color: 'var(--tc-color-info)' }}>{bulkRedundantLineCount}</strong> redundante(s)</>
                                    )}
                                    {bulkInvalidLineCount > 0 && (
                                        <>, <strong style={{ color: 'var(--tc-color-danger)' }}>{bulkInvalidLineCount}</strong> inválida(s)</>
                                    )}
                                </div>
                            )}
                        </VBox>
                    )}
                    {bulkRuleMode === 'category' && bulkCategories.length > 0 && (
                        <div style={{
                            padding: '8px 12px',
                            background: 'var(--tc-color-gray-50)',
                            borderRadius: '4px',
                            fontSize: '0.9em',
                            color: 'var(--tc-color-muted)'
                        }}>
                            {bulkCategories.length} regra(s) será(ão) criada(s)
                        </div>
                    )}
                    {bulkRuleMode === 'feed' && bulkFeeds.length > 0 && (
                        <div style={{
                            padding: '8px 12px',
                            background: 'var(--tc-color-gray-50)',
                            borderRadius: '4px',
                            fontSize: '0.9em',
                            color: 'var(--tc-color-muted)'
                        }}>
                            {bulkFeeds.length} regra(s) será(ão) criada(s)
                        </div>
                    )}
                </VBox>
            </Modal>
        </>
    );
}
